/**
 * The closing report for a shop receipt.
 *
 * There is exactly one builder here and both outputs go through it: the printed
 * slip and the PDF attached to the worker's email. Keeping a single source means
 * the paper copy and the emailed copy cannot drift apart, which is the whole
 * point of handing someone a receipt for goods they just counted.
 */

export interface ShopReceiptReportLine {
	kind: 'expected' | 'extra'
	sku: string | null
	product_name: string | null
	reference_code: string | null
	color: string | null
	size: string | null
	original_qty: number
	expected_qty: number
	received_qty: number
	missing_qty: number
	reported_missing: boolean
	scan_source: 'manual' | 'barcode' | 'epc'
}

export interface ShopReceiptReportInput {
	reservationNumber: string
	customerName?: string | null
	warehouseWorkerName?: string | null
	confirmedByName?: string | null
	mode: 'one_by_one' | 'scan'
	submittedAt: string
	lines: ShopReceiptReportLine[]
	company?: {
		company_name?: string | null
		logo_url?: string | null
		company_address?: string | null
		company_phone?: string | null
		company_email?: string | null
	} | null
}

export interface ShopReceiptTotals {
	receivedUnits: number
	missingUnits: number
	extraUnits: number
	receivedLines: ShopReceiptReportLine[]
	missingLines: ShopReceiptReportLine[]
	extraLines: ShopReceiptReportLine[]
	hasDiscrepancy: boolean
}

function escapeHtml(value: unknown): string {
	const str = value == null ? '' : String(value)
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

/** How many units of a line are missing, whichever way the shortfall arose. */
function shortfallOf(line: ShopReceiptReportLine): number {
	if (line.missing_qty > 0) return line.missing_qty
	// A line the warehouse never sent has expected_qty 0, so the generated
	// missing_qty is 0 too. When the shop explicitly reports it, the shortfall is
	// measured against what the customer reserved instead.
	if (line.reported_missing) return Math.max(line.original_qty - line.received_qty, 0)
	return 0
}

/**
 * Splits the lines into the three buckets the report is built around.
 *
 * A partially received line legitimately appears under both Received and
 * Missing: one unit arrived and two did not, and the report has to say both.
 * A line flagged in step 2 that was then counted in full is a contradiction the
 * worker can reach by going back a step, and it resolves to no shortfall rather
 * than a "0 missing" row.
 */
export function summariseShopReceipt(lines: ShopReceiptReportLine[]): ShopReceiptTotals {
	const expected = lines.filter((l) => l.kind === 'expected')
	const extraLines = lines.filter((l) => l.kind === 'extra' && l.received_qty > 0)

	const receivedLines = expected.filter((l) => l.received_qty > 0)
	const missingLines = expected.filter((l) => shortfallOf(l) > 0)

	return {
		receivedUnits: receivedLines.reduce((sum, l) => sum + l.received_qty, 0),
		missingUnits: missingLines.reduce((sum, l) => sum + shortfallOf(l), 0),
		extraUnits: extraLines.reduce((sum, l) => sum + l.received_qty, 0),
		receivedLines,
		missingLines,
		extraLines,
		hasDiscrepancy: missingLines.length > 0 || extraLines.length > 0,
	}
}

function describeLine(line: ShopReceiptReportLine): string {
	const variant = [line.color, line.size].filter(Boolean).join(' / ')
	return variant ? `${line.product_name || line.sku || 'Item'} (${variant})` : line.product_name || line.sku || 'Item'
}

const SCAN_SOURCE_LABEL: Record<ShopReceiptReportLine['scan_source'], string> = {
	manual: 'Manual',
	barcode: 'Barcode',
	epc: 'UHF tag',
}

function rowsFor(lines: ShopReceiptReportLine[], columns: 'received' | 'missing' | 'extra'): string {
	if (lines.length === 0) {
		return '<tr><td colspan="6" class="empty">None</td></tr>'
	}

	return lines
		.map((line) => {
			const qtyCells =
				columns === 'received'
					? `<td class="num">${escapeHtml(line.expected_qty)}</td><td class="num strong">${escapeHtml(line.received_qty)}</td>`
					: columns === 'missing'
					? `<td class="num">${escapeHtml(line.expected_qty > 0 ? line.expected_qty : line.original_qty)}</td><td class="num strong">${escapeHtml(
							line.missing_qty > 0 ? line.missing_qty : Math.max(line.original_qty - line.received_qty, 0)
					  )}</td>`
					: `<td class="num">-</td><td class="num strong">${escapeHtml(line.received_qty)}</td>`

			return `
				<tr>
					<td>${escapeHtml(describeLine(line))}</td>
					<td class="mono">${escapeHtml(line.sku || '-')}</td>
					<td>${escapeHtml(line.reference_code || '-')}</td>
					${qtyCells}
					<td class="src">${escapeHtml(SCAN_SOURCE_LABEL[line.scan_source] || 'Manual')}</td>
				</tr>`
		})
		.join('')
}

function section(
	title: string,
	accent: string,
	tint: string,
	lines: ShopReceiptReportLine[],
	units: number,
	columns: 'received' | 'missing' | 'extra',
	countedHeader: string
): string {
	return `
	<section>
		<h2 style="color:${accent};border-bottom-color:${accent};">
			${escapeHtml(title)}
			<span class="badge" style="background:${tint};color:${accent};">${escapeHtml(units)} unit${units === 1 ? '' : 's'}</span>
		</h2>
		<table>
			<thead>
				<tr style="background:${tint};">
					<th style="color:${accent};">Product</th>
					<th style="color:${accent};">SKU</th>
					<th style="color:${accent};">Reference</th>
					<th class="num" style="color:${accent};">Expected</th>
					<th class="num" style="color:${accent};">${escapeHtml(countedHeader)}</th>
					<th style="color:${accent};">Counted by</th>
				</tr>
			</thead>
			<tbody>${rowsFor(lines, columns)}</tbody>
		</table>
	</section>`
}

/** Builds the branded report document. Used for both print and the emailed PDF. */
export function buildShopReceiptReportHtml(input: ShopReceiptReportInput): string {
	const totals = summariseShopReceipt(input.lines)
	const company = input.company
	const companyName = company?.company_name || ''
	const submitted = new Date(input.submittedAt)

	const logo = company?.logo_url
		? `<img class="logo" src="${escapeHtml(company.logo_url)}" alt="${escapeHtml(companyName)}" crossorigin="anonymous" />`
		: ''

	const contact = [company?.company_address, company?.company_phone, company?.company_email]
		.filter(Boolean)
		.map((part) => escapeHtml(part))
		.join(' &middot; ')

	const meta = [
		['Reservation', input.reservationNumber],
		['Customer', input.customerName || '-'],
		['Picked by', input.warehouseWorkerName || '-'],
		['Confirmed by', input.confirmedByName || '-'],
		['Method', input.mode === 'scan' ? 'Scanner' : 'One by one'],
		['Confirmed at', submitted.toLocaleString()],
	]
		.map(
			([label, value]) =>
				`<div class="kv"><span class="kv-label">${escapeHtml(label)}</span><span class="kv-value">${escapeHtml(value)}</span></div>`
		)
		.join('')

	const verdict = totals.hasDiscrepancy
		? `<div class="verdict warn">Discrepancy recorded. This report is for the record only - no stock was changed.</div>`
		: `<div class="verdict ok">Delivery matched in full. No stock was changed.</div>`

	return `<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8" />
	<title>Shop Receipt - ${escapeHtml(input.reservationNumber)}</title>
	<style>
		* { box-sizing: border-box; }
		body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 36px; color: #1f2937; font-size: 13px; margin: 0; background: #fff; }
		.header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; border-bottom: 3px solid #0891b2; padding-bottom: 16px; margin-bottom: 20px; }
		.logo { max-height: 56px; max-width: 180px; object-fit: contain; }
		.title { font-size: 24px; font-weight: 700; color: #0e7490; margin: 0; }
		.subtitle { color: #6b7280; margin-top: 4px; }
		.company { text-align: right; }
		.company-name { font-weight: 700; font-size: 15px; }
		.company-contact { color: #6b7280; font-size: 11px; margin-top: 4px; max-width: 260px; }
		.meta { display: flex; flex-wrap: wrap; gap: 8px 28px; margin-bottom: 18px; }
		.kv { display: flex; flex-direction: column; }
		.kv-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; font-weight: 600; }
		.kv-value { font-weight: 600; color: #111827; }
		.totals { display: flex; gap: 12px; margin-bottom: 22px; }
		.tile { flex: 1; border-radius: 10px; padding: 12px 14px; border: 1px solid; }
		.tile-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
		.tile-value { font-size: 26px; font-weight: 800; line-height: 1.1; margin-top: 2px; }
		.tile-sub { font-size: 10px; color: #6b7280; }
		.verdict { border-radius: 8px; padding: 10px 14px; font-weight: 600; margin-bottom: 22px; font-size: 12px; }
		.verdict.ok { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
		.verdict.warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
		section { margin-bottom: 22px; page-break-inside: avoid; }
		h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid; padding-bottom: 6px; margin: 0 0 8px; display: flex; align-items: center; gap: 8px; }
		.badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; letter-spacing: 0; text-transform: none; font-weight: 700; }
		table { width: 100%; border-collapse: collapse; }
		th { text-align: left; padding: 7px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; }
		td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; }
		td.num, th.num { text-align: center; }
		td.strong { font-weight: 700; }
		td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; }
		td.src { color: #6b7280; font-size: 11px; }
		td.empty { color: #9ca3af; font-style: italic; text-align: center; padding: 12px; }
		.footer { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 10px; display: flex; justify-content: space-between; }
		@media print {
			body { padding: 14px; }
			.no-print { display: none; }
			* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
			@page { size: A4; margin: 12mm; }
		}
	</style>
</head>
<body>
	<div class="header">
		<div>
			<h1 class="title">Shop Receipt</h1>
			<div class="subtitle">Shop-side confirmation of a warehouse delivery</div>
		</div>
		<div class="company">
			${logo}
			${companyName ? `<div class="company-name">${escapeHtml(companyName)}</div>` : ''}
			${contact ? `<div class="company-contact">${contact}</div>` : ''}
		</div>
	</div>

	<div class="meta">${meta}</div>

	<div class="totals">
		<div class="tile" style="background:#ecfdf5;border-color:#a7f3d0;">
			<div class="tile-label" style="color:#047857;">Received</div>
			<div class="tile-value" style="color:#047857;">${escapeHtml(totals.receivedUnits)}</div>
			<div class="tile-sub">${escapeHtml(totals.receivedLines.length)} line${totals.receivedLines.length === 1 ? '' : 's'}</div>
		</div>
		<div class="tile" style="background:#fffbeb;border-color:#fde68a;">
			<div class="tile-label" style="color:#b45309;">Missing</div>
			<div class="tile-value" style="color:#b45309;">${escapeHtml(totals.missingUnits)}</div>
			<div class="tile-sub">${escapeHtml(totals.missingLines.length)} line${totals.missingLines.length === 1 ? '' : 's'}</div>
		</div>
		<div class="tile" style="background:#f5f3ff;border-color:#ddd6fe;">
			<div class="tile-label" style="color:#6d28d9;">Extra</div>
			<div class="tile-value" style="color:#6d28d9;">${escapeHtml(totals.extraUnits)}</div>
			<div class="tile-sub">${escapeHtml(totals.extraLines.length)} line${totals.extraLines.length === 1 ? '' : 's'}</div>
		</div>
	</div>

	${verdict}

	${section('Received', '#047857', '#ecfdf5', totals.receivedLines, totals.receivedUnits, 'received', 'Received')}
	${section('Missing', '#b45309', '#fffbeb', totals.missingLines, totals.missingUnits, 'missing', 'Missing')}
	${section('Extra', '#6d28d9', '#f5f3ff', totals.extraLines, totals.extraUnits, 'extra', 'Received')}

	<div class="footer">
		<span>Generated by the Shop Terminal. Audit record only - stock is unchanged.</span>
		<span>${escapeHtml(new Date().toLocaleString())}</span>
	</div>
</body>
</html>`
}

/**
 * Sends the report to the printer.
 *
 * Every other print path in this app uses `window.open` + `document.write`, so
 * that stays the primary route for consistency. It is not reliable on a tablet
 * though: iOS Safari and locked-down kiosk browsers block the popup, and the
 * button would then silently do nothing with a shop worker standing over it.
 * The iframe fallback prints from the current document instead, which no popup
 * blocker can stop.
 */
export function printShopReceiptReport(html: string): void {
	const popup = window.open('', '_blank', 'width=980,height=760')

	if (popup) {
		popup.document.write(
			html.replace('</body>', '<script>window.onload = function () { window.print(); };</script></body>')
		)
		popup.document.close()
		return
	}

	const frame = document.createElement('iframe')
	frame.setAttribute('aria-hidden', 'true')
	frame.style.position = 'fixed'
	frame.style.right = '0'
	frame.style.bottom = '0'
	frame.style.width = '0'
	frame.style.height = '0'
	frame.style.border = '0'
	document.body.appendChild(frame)

	const doc = frame.contentDocument
	if (!doc) {
		document.body.removeChild(frame)
		return
	}

	doc.open()
	doc.write(html)
	doc.close()

	const run = () => {
		try {
			frame.contentWindow?.focus()
			frame.contentWindow?.print()
		} finally {
			// Leave the frame in place long enough for the print dialog to read it.
			window.setTimeout(() => {
				if (frame.parentNode) frame.parentNode.removeChild(frame)
			}, 60_000)
		}
	}

	// Images need a moment; the report carries the company logo and nothing else
	// heavy, so a short settle beats waiting on a load event that may not fire.
	window.setTimeout(run, 350)
}
